/**
 * サブ情報フォーム（共通コンポーネント）
 * RoomQrEntryPage およびサブ情報編集画面で使用する
 */
import { useEffect, useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormControl,
  CircularProgress,
  Chip,
  Typography,
} from '@mui/material'
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import { RoomSubFieldDef, SubFormValues } from '@/types/roomSubInfo'

// ── 型バッジ定義 ──────────────────────────────────────────
export const SUB_FIELD_TYPE_LABELS: Record<RoomSubFieldDef['type'], string> = {
  text: '文字列',
  number: '数値',
  date: '日付',
  checkbox: 'チェック',
}
export const SUB_FIELD_TYPE_COLORS: Record<
  RoomSubFieldDef['type'],
  'primary' | 'secondary' | 'success' | 'warning'
> = {
  text: 'primary',
  number: 'secondary',
  date: 'success',
  checkbox: 'warning',
}

export const buildSubInfoInitialValues = (defs: RoomSubFieldDef[]): SubFormValues =>
  Object.fromEntries(
    defs.map((d) => [d.id, d.type === 'checkbox' ? false : ''])
  )

// ── Props ─────────────────────────────────────────────────
interface SubInfoFormProps {
  fieldDefs: RoomSubFieldDef[]
  onComplete: (values: SubFormValues) => void
  submitLabel?: string
  /** フォームサイズ（stepper内では small を推奨） */
  fieldSize?: 'small' | 'medium'
  initialValues?: SubFormValues
  /** 入力値が変化するたびに呼ばれる（ステップ遷移時の入力保持などに利用） */
  onValuesChange?: (values: SubFormValues) => void
}

export const SubInfoForm = ({
  fieldDefs,
  onComplete,
  submitLabel = '保存して次へ',
  fieldSize = 'small',
  initialValues,
  onValuesChange,
}: SubInfoFormProps) => {
  const [values, setValues] = useState<SubFormValues>(buildSubInfoInitialValues(fieldDefs))
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const defaults = buildSubInfoInitialValues(fieldDefs)
    setValues({ ...defaults, ...(initialValues ?? {}) })
  }, [fieldDefs, initialValues])

  const handleChange = (id: string, value: string | number | boolean) => {
    setValues((prev) => {
      const next = { ...prev, [id]: value }
      onValuesChange?.(next)
      return next
    })
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      await onComplete(values)
    } finally {
      setLoading(false)
    }
  }

  const renderField = (def: RoomSubFieldDef) => {
    const value = values[def.id]

    return (
      <Box key={def.id} sx={{ mb: 2.5 }}>
        {/* ラベル + タイプバッジ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: def.type === 'checkbox' ? 0 : 0.5 }}>
          {def.type !== 'checkbox' && (
            <Typography variant="body2" fontWeight={600}>
              {def.label}
            </Typography>
          )}
          <Chip
            label={SUB_FIELD_TYPE_LABELS[def.type]}
            size="small"
            color={SUB_FIELD_TYPE_COLORS[def.type]}
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>

        {def.type === 'text' && (
          <TextField
            fullWidth size={fieldSize} placeholder={def.placeholder} value={value as string}
            onChange={(e) => handleChange(def.id, e.target.value)}
            helperText={def.helperText}
          />
        )}
        {def.type === 'number' && (
          <TextField
            fullWidth size={fieldSize} type="number" placeholder={def.placeholder} value={value as string}
            onChange={(e) => handleChange(def.id, e.target.value)}
            helperText={def.helperText}
            inputProps={{ inputMode: 'numeric' }}
          />
        )}
        {def.type === 'date' && (
          <TextField
            fullWidth size={fieldSize} type="date" value={value as string}
            onChange={(e) => handleChange(def.id, e.target.value)}
            helperText={def.helperText}
            InputLabelProps={{ shrink: true }}
          />
        )}
        {def.type === 'checkbox' && (
          <FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value as boolean} size={fieldSize}
                  onChange={(e) => handleChange(def.id, e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {def.label}
                </Typography>
              }
            />
            {def.helperText && (
              <FormHelperText sx={{ mt: -0.5, ml: 4 }}>{def.helperText}</FormHelperText>
            )}
          </FormControl>
        )}
      </Box>
    )
  }

  return (
    <Box>
      {fieldDefs.map(renderField)}
      <Button
        variant="contained" fullWidth size="large"
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
        onClick={handleSubmit} disabled={loading}
        sx={{ mt: 1, borderRadius: 2, py: 1.2, fontWeight: 700 }}
      >
        {loading ? '処理中…' : submitLabel}
      </Button>
    </Box>
  )
}
